import { BrowserModule } from "@angular/platform-browser";
import { NgModule, APP_INITIALIZER, inject, CSP_NONCE } from "@angular/core";
import { AppRoutingModule } from "./app-routing.module";
import { ReactiveFormsModule } from "@angular/forms";
import { ToastrModule } from "ngx-toastr";

/*Main*/
import { AppComponent } from "./app.component";
import { HeaderComponent } from "./layout/header/header.component";

/*Services*/
import { AuthGuard } from "./service/authGuard/auth.guard";
import { ConfigService } from "./service/config/config.service";
import { AuthService } from "./service/auth/auth.service";
import { TokenInterceptor } from "./service/auth/token-interceptor/token-interceptor.service";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { PopupUnauthorizedComponent } from "./layout/popup-unauthorized/popup-unauthorized.component";
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { APP_BASE_HREF, CommonModule, PlatformLocation } from "@angular/common";
import { StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { environment } from "../environments/environment";
import { EffectsModule } from "@ngrx/effects";
import { reducers, metaReducers } from "./@store/reducers";
import { effects } from "./@store/effects";

/** */
import dayjs from "dayjs";
import tz from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import localeData from "dayjs/plugin/localeData";
import toArray from "dayjs/plugin/toArray";
import localizedFormat from "dayjs/plugin/localizedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { AssetLinkPipe } from "./shared/pipes/asset-link.pipe";
import { PdfModalComponent } from "./shared/components/pdf-modal/pdf-modal.component";
import { PdfViewerModule } from "ng2-pdf-viewer";
import { TranslocoRootModule } from "./transloco-root.module";
import { LanguagePickerComponent } from "./shared/components/language-picker/language-picker.component";
import { MatIconModule } from "@angular/material/icon";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MessageDialogComponent } from "./shared/components/message-dialog/message-dialog.component";
import { MatDialogModule } from "@angular/material/dialog";
import { LabOrderComponent } from "./pages/lab-order/lab-order.component";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";

import { MatFormFieldModule } from "@angular/material/form-field";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { NgxMatTimepickerModule } from "ngx-mat-timepicker";
import { MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from "@angular/material/core";
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ImgPreloaderService } from "./service/img-preloader/img-preloader.service";
import { SynlabHeaderSwitchProfileModule } from "./shared/component-modules/synlab-header-switch-profile/synlab-header.component";
import { ConfigService as LibConfigService, UsertypeService } from "./plasma-ui-common/synlab-access-ui-components/core";
import { LoggerService as LibLoggerService } from "./plasma-ui-common/synlab-access-ui-components/core";
import { HeaderUserTypeService } from "./service/header/header-user-type.service";
import { HeaderConfigService } from "./service/header/header-config.service";
import { ProfileService } from "./plasma-ui-common/synlab-access-ui-components/core/profile/profile.service";
import { LoggerService } from "./service/logger.service";
import { HeaderVisibilityService as LibHeaderVisibilityService } from "./plasma-ui-common/synlab-access-ui-components/core/header-visibility/header-visibility.service";
import { HeaderVisibilityService } from "./service/header/header-visibility.service";
import { RANDOM_NONCE } from "./plasma-ui-common/synlab-access-ui-components/core/constants";
import { ShimmerLoadingPatientwebComponent } from "./plasma-ui-common/synlab-access-ui-components/shimmer-loading-patientweb/shimmer-loading-patientweb.component";
import { OrganizationInterceptor } from "./services/organization.interceptor";
import { OrganizationService } from "./services/organization.service";
import { OrganizationService as LibOrganizationService } from './plasma-ui-common/synlab-access-ui-components/core/organization/organization.service';

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/prevention-wellness/', '.json');
}

const globalRippleConfig: RippleGlobalOptions = {
  disabled: true,
  animation: {
    enterDuration: 300,
    exitDuration: 0
  }
};

@NgModule({
  declarations: [
    AppComponent,
    PopupUnauthorizedComponent,
    LanguagePickerComponent,
    PdfModalComponent,
    MessageDialogComponent,
    AssetLinkPipe,
    LabOrderComponent,
    HeaderComponent,
  ],
  imports: [
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
      },
    }),
    CommonModule,
    BrowserModule,
    NgxMatTimepickerModule,
    AppRoutingModule,
    HttpClientModule,
    MatIconModule,
    MatCheckboxModule,
    MatDialogModule,
    BrowserAnimationsModule,
    PdfViewerModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatFormFieldModule,
    ToastrModule.forRoot({
      timeOut: 1500,
      positionClass: "toast-top-right",
    }),
    EffectsModule.forRoot(effects),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
      connectInZone: true
    }),
    TranslocoRootModule,
    ReactiveFormsModule,
    NgIdleKeepaliveModule.forRoot(),
    SynlabHeaderSwitchProfileModule,
    ShimmerLoadingPatientwebComponent,
  ],
  providers: [
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: globalRippleConfig
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [ConfigService],
      useFactory: (cfg: ConfigService = inject(ConfigService)) => () => {
        cfg.load().then(c => {});
      },
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [ConfigService, OrganizationService],
      useFactory: (configService: ConfigService, organizationService: OrganizationService) => {
        const init = async () => {
          await configService.loadEnvJson();
          await configService.load();
          await organizationService.loadCurrentOrganization();
        };
        return () => init();
      },
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [ImgPreloaderService],
      useFactory: (preloader: ImgPreloaderService) => {
        return () => {
          return preloader.preloadImages();
        };
      },
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => () => {
        dayjs.extend(tz);
        dayjs.extend(utc);
        dayjs.extend(localeData);
        dayjs.extend(toArray);
        dayjs.extend(localizedFormat);
        dayjs.extend(customParseFormat);
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OrganizationInterceptor,
      multi: true,
    },
    {
      provide: APP_BASE_HREF,
      useFactory: (s: PlatformLocation) => s.getBaseHrefFromDOM(),
      deps: [PlatformLocation],
    },
    {
      provide: LibOrganizationService,
      useClass: OrganizationService,
    },
    AuthGuard,
    AuthService,
    AssetLinkPipe,
    LoggerService,
    {
      provide: UsertypeService,
      useClass: HeaderUserTypeService
    },
    {
      provide: LibConfigService,
      useClass: HeaderConfigService
    },
    {
      provide: LibLoggerService,
      useClass: LoggerService
    },
    {
      provide: ProfileService,
      useClass: HeaderUserTypeService
    },
    {
      provide: LibHeaderVisibilityService,
      useClass: HeaderVisibilityService
    },
    {
      provide: CSP_NONCE,
      useValue: RANDOM_NONCE
    }
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
